import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { Icon } from '~/components/granular';
import { classNames } from '~/utils/styles';
import type { InputProps } from './Input.types';

const inputVariants = cva(
  'body-200-light w-full min-w-40 border border-solid border-neutral-400 px-2 py-2 hover:border-teal-700 focus:border-teal-800 focus:outline-1 focus:outline-teal-700 disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-200 disabled:text-neutral-600',
  {
    // Only pill-height filters get the compact text; at form heights they keep the body text of the fields around them.
    compoundVariants: [{ appearance: 'filter', class: 'text-sm leading-4', height: ['x-small', 'small'] }],
    defaultVariants: {
      appearance: 'default',
      height: 'medium',
    },
    variants: {
      appearance: {
        default: 'text-neutral-1000 placeholder:text-neutral-600 focus:outline-solid',
        filter: 'cursor-pointer',
      },
      height: {
        large: 'h-10',
        medium: 'h-9',
        small: 'h-8',
        'x-small': 'h-7',
      },
    },
  },
);

const translateXClasses = 'absolute top-1/2 -translate-y-1/2 transform text-neutral-600 cursor-default body-400-light';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      appearance = 'default',
      appendLeft,
      appendRight,
      className,
      clearable,
      disabled,
      hasError,
      height = 'medium',
      id,
      leftIcon,
      onClear,
      placeholder,
      rightIcon,
      showSelectedState = true,
      type = 'text',
      value,
      ...props
    },
    forwardedRef,
  ) => {
    const selectedStateEnabled = showSelectedState && !!value && appearance === 'filter';
    const showClearButton = !!clearable && !!value && !disabled;
    const appendRightIsString = appendRight && typeof appendRight === 'string';
    const appendRightIsNode = appendRight && typeof appendRight !== 'string';
    const hasRightSlot = showClearButton || appendRight;

    const classes = classNames(
      inputVariants({ appearance, height }),
      {
        'border-red-800 hover:border-red-800 focus:border-red-800 focus:outline-red-800': hasError,
        'border-teal-500 bg-teal-100 text-teal-700': selectedStateEnabled,
        'pl-[70px]': appendLeft && typeof appendLeft !== 'string',
        // small padding if we add strings, bigger if we add elements
        // ideally, we want to calculate it dynamically, but that's not easy thing with tailwind
        'pl-7': leftIcon || (appendLeft && typeof appendLeft === 'string'),
        'pr-[90px]': appendRightIsNode && showClearButton,
        'pr-[70px]': appendRightIsNode && !showClearButton,
        'pr-12': appendRightIsString && showClearButton,
        'pr-7': (appendRightIsString && !showClearButton) || (!appendRight && (rightIcon || showClearButton)),
      },
      className,
    );

    return (
      <div className="relative">
        {appendLeft ? (
          <div className={classNames(translateXClasses, 'left-2 text-left')}>{appendLeft}</div>
        ) : leftIcon ? (
          <Icon
            className={classNames(translateXClasses, 'left-2', {
              'cursor-not-allowed': disabled,
              'text-red-800': hasError,
              'text-teal-700': selectedStateEnabled,
            })}
            height={16}
            icon={leftIcon}
            role="img"
            width={16}
          />
        ) : null}
        <input
          aria-placeholder={placeholder}
          className={classes}
          disabled={disabled}
          id={id}
          placeholder={placeholder}
          ref={forwardedRef}
          type={type}
          value={value}
          {...props}
        />
        {hasRightSlot ? (
          <div className={classNames(translateXClasses, 'right-2 flex items-center gap-1')}>
            {appendRight ? <div className="text-right">{appendRight}</div> : null}
            {showClearButton ? (
              <button aria-label="Clear" className="cursor-pointer text-right" onClick={onClear} type="button">
                <Icon height={16} icon="CloseIcon" role="img" width={16} />
              </button>
            ) : null}
          </div>
        ) : rightIcon ? (
          <Icon
            className={classNames(translateXClasses, 'right-2', {
              'cursor-not-allowed': disabled,
              'text-red-800': hasError,
              'text-teal-700': selectedStateEnabled,
            })}
            height={16}
            icon={rightIcon}
            role="img"
            width={16}
          />
        ) : null}
      </div>
    );
  },
);
